import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface DashboardDetail {
    requestId: number;
    requestSeqId: number;
    requestDt: string;
    shiftDayId: number;
    requestStatus: string;
    requiredSkill: string | null;
    requiredTeam: string | null;
    claimedByUserId: string | null;
    claimedByUserNm: string | null;
    postedByUserId: string | null;
    postedByUserNm: string | null;
    requestType: string | null;
    startTime: string | null;
    endTime: string | null;
    syncedToWorkday: boolean;
}

export interface DashboardResponse {
    details: DashboardDetail[];
    rejectedShifts: DashboardDetail[];
}

interface DashboardReportState {
    dashboardData: DashboardDetail[];
    rejectedShifts: DashboardDetail[];
    loading: boolean;
    error: string | null;
}

const initialState: DashboardReportState = {
    dashboardData: [],
    rejectedShifts: [],
    loading: false,
    error: null,
};

// ========================================
// HELPERS
// ========================================
/**
 * Normalize shift type abbreviations by stripping the "-D" (direct-report) suffix.
 * e.g. "SFT-D" -> "SFT", "VTO-D" -> "VTO". The dashboard does not need to
 * track direct-report-only shifts separately for notifications/grabs.
 */
const normalizeShiftType = (shiftType: string | null): string | null => {
    if (!shiftType) return shiftType;
    return shiftType.replace(/-D$/i, '');
};

const normalizeDetail = (detail: DashboardDetail): DashboardDetail => ({
    ...detail,
    requestType: normalizeShiftType(detail.requestType),
});

// ========================================
// ASYNC THUNKS
// ========================================
/**
 * Fetch dashboard report data for a site with optional date range
 * Uses: ENDPOINTS.GET_DASHBOARD_REPORT + siteCd + optional startDate/endDate
 */
export const fetchDashboardReport = createAsyncThunk(
    'dashboardReport/fetchDashboardReport',
    async (
        { siteCd, startDate, endDate }: { siteCd: string; startDate?: string; endDate?: string },
        { rejectWithValue }
    ): Promise<DashboardResponse | ReturnType<typeof rejectWithValue>> => {
        try {
            const params: string[] = [`siteCd=${siteCd}`];
            if (startDate) params.push(`startDate=${startDate}`);
            if (endDate) params.push(`endDate=${endDate}`);

            const url = `${ENDPOINTS.GET_DASHBOARD_REPORT}?${params.join('&')}`;
            const response = await API_FETCH(url);
            console.log('✅ Dashboard Report API Response:', response);

            const rawDetails = response?.details ?? response?.Details;
            const rawRejected = response?.rejectedShifts ?? response?.RejectedShifts;
            return {
                details: Array.isArray(rawDetails) ? rawDetails.map(normalizeDetail) : [],
                rejectedShifts: Array.isArray(rawRejected) ? rawRejected.map(normalizeDetail) : [],
            };
        } catch (error: any) {
            console.error('❌ Dashboard Report API Error:', error);
            return rejectWithValue(error?.message || 'Failed to fetch dashboard report data');
        }
    }
);

// ========================================
// SLICE
// ========================================
const dashboardReportSlice = createSlice({
    name: 'dashboardReport',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDashboardReport.fulfilled, (state, action) => {
                state.loading = false;
                state.dashboardData = Array.isArray(action.payload.details) ? action.payload.details : [];
                state.rejectedShifts = Array.isArray(action.payload.rejectedShifts) ? action.payload.rejectedShifts : [];
            })
            .addCase(fetchDashboardReport.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = dashboardReportSlice.actions;
export default dashboardReportSlice.reducer;